import { sleep } from '@/presentation/hooks/sleep';

/**
 * Backoff exponencial compartilhado pelas duas fases retentáveis do
 * carregamento de plugin (HUB-237): resolução do registro
 * (`usePluginRegistryResolution`, CA-16) e carregamento via Module
 * Federation (`usePluginLoadRetry`, CA-06/07/08). Mesma sequência
 * (1s/2s/4s, 3 tentativas) nas duas fases — CA-19: soma até ~7s antes do
 * botão de retentativa manual aparecer.
 */
export const RETRY_BACKOFF_MS: readonly number[] = [1_000, 2_000, 4_000];

export const MAX_LOAD_ATTEMPTS = RETRY_BACKOFF_MS.length;

// `RETRY_BACKOFF_MS[MAX_LOAD_ATTEMPTS - 1]` sempre existe em tempo de
// execução (array fixo, não vazio) — o fallback abaixo só satisfaz
// `noUncheckedIndexedAccess`, nunca é de fato alcançado.
const LAST_BACKOFF_MS = RETRY_BACKOFF_MS[MAX_LOAD_ATTEMPTS - 1] ?? 4_000;

/**
 * Aguarda o backoff da tentativa `failedAttempt` (1-indexado), reportando o
 * countdown em segundos a cada tick via `onTick` (Design > Estado "Erro com
 * retry automático em andamento": countdown visual obrigatório, CA-06/07/
 * 08). Retorna cedo (sem completar o countdown) se `isCancelled()` passar a
 * ser `true` no meio da espera — evita `setState` após o componente
 * desmontar.
 */
export async function waitBackoff(
  failedAttempt: number,
  onTick: (secondsRemaining: number) => void,
  isCancelled: () => boolean
): Promise<void> {
  const backoffMs = RETRY_BACKOFF_MS[failedAttempt - 1] ?? LAST_BACKOFF_MS;
  let secondsRemaining = Math.ceil(backoffMs / 1_000);
  onTick(secondsRemaining);
  while (secondsRemaining > 0) {
    await sleep(1_000);
    if (isCancelled()) return;
    secondsRemaining -= 1;
    if (secondsRemaining > 0) onTick(secondsRemaining);
  }
}
